import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, TrendingUp, TrendingDown, Trophy, Target, Gamepad2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatCoins } from "../lib/format";

const gameLabel = { spin: "Spin", color: "Color", dice: "Dice", scratch: "Scratch", aviator: "Aviator", teenpatti: "Teen Patti", andarbahar: "Andar Bahar" };
const GAME_FILTERS = ["all", "aviator", "spin", "color", "dice", "scratch", "teenpatti", "andarbahar"];

export default function Wallet() {
  const { user, setCoins, refresh } = useAuth();
  const { t } = useLang();
  const [tx, setTx] = useState([]);
  const [filter, setFilter] = useState("all");
  const [gstats, setGstats] = useState([]);

  const load = () => api.get("/wallet/transactions").then(({ data }) => setTx(data)).catch(() => {});
  useEffect(() => { refresh(); load(); api.get("/games/stats").then(({ data }) => setGstats(data)).catch(() => {}); }, []);

  const stats = [
    { icon: Coins, label: t("balance"), value: formatCoins(user?.coins), color: "#FFD700" },
    { icon: Trophy, label: t("totalWon"), value: formatCoins(user?.total_won), color: "#00E676" },
    { icon: Target, label: t("totalWagered"), value: formatCoins(user?.total_wagered), color: "#00F0FF" },
    { icon: Gamepad2, label: t("gamesPlayed"), value: Number(user?.games_played || 0).toLocaleString("en-IN"), color: "#9D4EDD" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("wallet")}</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="mw-card p-4" data-testid={`wallet-stat-${s.label}`}>
            <s.icon size={20} style={{ color: s.color }} />
            <p className="text-xs uppercase tracking-wider text-slate-400 mt-2">{s.label}</p>
            <p className="font-mono-num text-xl font-bold mt-0.5" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="mw-card p-6" data-testid="deposit-cta">
          <h2 className="font-display text-lg font-bold mb-2">{t("addMoney")}</h2>
          <p className="text-sm text-slate-400 mb-3">{t("depositHint")}</p>
          <Link to="/app/payments" className="inline-flex items-center gap-2 btn-gold rounded-xl px-4 py-2 text-sm font-semibold">
            ₹ {t("addMoney")}
          </Link>
        </div>
      </div>

      {gstats.length > 0 && (
        <div className="mw-card p-6" data-testid="game-stats-card">
          <h2 className="font-display text-xl font-bold mb-4">{t("gameStats")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {gstats.map((s) => (
              <div key={s.game} className="rounded-xl border border-white/5 bg-black/20 p-3" data-testid={`game-stat-${s.game}`}>
                <p className="text-sm font-bold text-amber-200 capitalize">{gameLabel[s.game] || s.game}</p>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-500">{t("bestWin")}</span><span className="font-mono-num text-emerald-400 font-bold">+{formatCoins(s.bestWin)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">{t("bestMulti")}</span><span className="font-mono-num text-cyan-300 font-bold">{s.bestMult.toFixed(2)}×</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">{t("roundsPlayed")}</span><span className="font-mono-num text-slate-300">{s.rounds}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mw-card p-6">
        <h2 className="font-display text-xl font-bold mb-4">{t("transactions")}</h2>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="history-filters">
          {GAME_FILTERS.map((g) => (
            <button key={g} onClick={() => setFilter(g)} data-testid={`history-filter-${g}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                filter === g ? "btn-gold" : "border border-amber-500/30 bg-black/30 text-amber-200 hover:border-amber-500/70"
              }`}>
              {g === "all" ? t("allGames") : (gameLabel[g] || g)}
            </button>
          ))}
        </div>
        <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="transactions-list">
          {(() => {
            const rows = filter === "all" ? tx : tx.filter((r) => r.game === filter);
            if (rows.length === 0) return <p className="text-slate-500 text-sm">—</p>;
            return rows.map((r) => (
              <div key={r.round_id} className="flex items-center justify-between rounded-lg border border-white/5 bg-black/20 px-3 py-2">
                <div className="flex items-center gap-2">
                  {r.net >= 0 ? <TrendingUp size={16} className="text-emerald-400" /> : <TrendingDown size={16} className="text-rose-400" />}
                  <span className="text-sm font-semibold">{gameLabel[r.game] || r.game}</span>
                  <span className="text-xs text-slate-500">{t("bet")} {formatCoins(r.bet)}</span>
                </div>
                <span className={`font-mono-num font-bold text-sm ${r.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {r.net >= 0 ? "+" : ""}{formatCoins(r.net)}
                </span>
              </div>
            ));
          })()}
        </div>
      </div>
    </div>
  );
}
